import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { v4 as uuidv4 } from 'uuid'
import { PDFDocument } from 'pdf-lib';

const API_URL = process.env.REACT_APP_API_URL;

export default function DocumentsPage() {
  const { caseId } = useParams();
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemPostFilingRequirement, setNewItemPostFilingRequirement] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [updatingPostFilingId, setUpdatingPostFilingId] = useState(null);
  const [postFilingMessage, setPostFilingMessage] = useState('');
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [rejectingUpload, setRejectingUpload] = useState(null);
  const [deletingUploadId, setDeletingUploadId] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [mergeSelections, setMergeSelections] = useState({}); // { [itemId]: { ids: Set<number>, deleteOld: boolean, merging: boolean } }

  // Bank statements template: start month is 7 months ago
  const bankStatementsStart = new Date();
  bankStatementsStart.setMonth(bankStatementsStart.getMonth() - 7);
  const bankStatementsStartLabel = bankStatementsStart.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const checklistTemplates = [
    {
      label: "Current Year Tax Return",
      name: `${new Date().getFullYear() - 1} Tax Return`,
      description: 'Provide a copy of your full 1040 tax return OR tax transcript for the current year. You may obtain the "Tax Return" transcripts at https://www.irs.gov.',
    },
    {
      label: "Previous Year Tax Return",
      name: `${new Date().getFullYear() - 2} Tax Return`,
      description: 'Provide a copy of your full 1040 tax return OR tax transcript for the prior year. You may obtain the "Tax Return" transcripts at https://www.irs.gov.',
    },
    {
      label: "Two Years Ago Tax Return",
      name: `${new Date().getFullYear() - 3} Tax Return`,
      description: 'Provide a copy of your full 1040 tax return OR tax transcript for this year. You may obtain the "Tax Return" transcripts at https://www.irs.gov.',
    },
    {
      label: "Three Years Ago Tax Return",
      name: `${new Date().getFullYear() - 4} Tax Return`,
      description: 'Provide a copy of your full 1040 tax return OR tax transcript for this year. You may obtain the "Tax Return" transcripts at https://www.irs.gov.',
    },
    {
      label: "Recent Paystub",
      name: "Recent Paystub",
      description: "Provide your most recent paystub from your employment.",
    },
    {
      label: "Nonfiling Spouse Refund Affidavit",
      name: "Nonfiling Spouse Refund Affidavit",
      description: "Affidavit signed by your nonfiling spouse acknowledging that any joint tax refunds are subject to your bankruptcy plan.",
    },
    {
      label: "Tax Affidavit (Not Required to File)",
      name: "Tax Affidavit",
      description: "Affidavit stating that you are not required to file taxes.",
    },
    {
      label: "Marital Disolution Agreement",
      name: "Marital Disolution Agreement",
      description: "Agreement that sets out how the property and debts were split after divorce.",
    },
    {
      label: "Vehicle Registration",
      name: "Vehicle Registration - ",
      description: "Registration, photo of license plate or license plate number, or anything with a VIN #.",
    },
    {
      label: "Mortgage Statement",
      name: "Mortgage Statement",
      description: "Most recent mortgage statement for your home showing no late fees have been paid",
    },
    {
      label: "Bank Statements - last 6 months",
      name: `Bank Statements - ${bankStatementsStartLabel} to Present`,
      description: `Bank statements from ${bankStatementsStartLabel} to present (all pages, all accounts).`,
    },
    {
      label: "Bank Statements - Prior & Current Year",
      name: `Bank Statements - ${new Date().getFullYear() - 1} and ${new Date().getFullYear()} to Present`,
      description: `Provide bank statements for the full calendar year ${new Date().getFullYear() - 1} (January–December) and for ${new Date().getFullYear()} year-to-date (January to present) for all accounts (all pages).`,
    },
  ];
  useEffect(() => {
    if (!caseId) return;
    fetchChecklist();
  }, [caseId]);

  const fetchChecklist = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setChecklistItems(data);
    } catch (error) {
      console.error('Failed to fetch checklist items', error);
    } finally {
      setLoading(false);
    }
  };

  // --- PDF merge helpers ---
  const getMergeState = (itemId) => {
    const s = mergeSelections[itemId];
    return {
      ids: s?.ids || new Set(),
      deleteOld: !!s?.deleteOld,
      merging: !!s?.merging,
    };
  };

  const toggleMergeSelect = (itemId, uploadId) => {
    setMergeSelections(prev => {
      const cur = prev[itemId] || { ids: new Set(), deleteOld: false, merging: false };
      const nextIds = new Set(cur.ids);
      if (nextIds.has(uploadId)) nextIds.delete(uploadId);
      else nextIds.add(uploadId);
      return {
        ...prev,
        [itemId]: { ...cur, ids: nextIds },
      };
    });
  };

  const setMergeDeleteOld = (itemId, value) => {
    setMergeSelections(prev => {
      const cur = prev[itemId] || { ids: new Set(), deleteOld: false, merging: false };
      return {
        ...prev,
        [itemId]: { ...cur, deleteOld: !!value },
      };
    });
  };

  const clearMergeSelection = (itemId) => {
    setMergeSelections(prev => {
      const cur = prev[itemId];
      if (!cur) return prev;
      return {
        ...prev,
        [itemId]: { ...cur, ids: new Set() },
      };
    });
  };

  const isPdfUpload = (u) => {
    const name = (u?.filename || '').toLowerCase();
    return name.endsWith('.pdf');
  };

  const toggleSelectAllMergeableUploads = (item) => {
    const pdfIds = (item.uploads || [])
      .filter(isPdfUpload)
      .map(upload => upload.id);
    if (!pdfIds.length) return;

    setMergeSelections(prev => {
      const cur = prev[item.id] || { ids: new Set(), deleteOld: false, merging: false };
      const allSelected = pdfIds.every(uploadId => cur.ids.has(uploadId));
      return {
        ...prev,
        [item.id]: {
          ...cur,
          ids: allSelected ? new Set() : new Set(pdfIds),
        },
      };
    });
  };

  const handleAccept = async (uploadId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/uploaded-document/${uploadId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'accepted', review_notes: '' })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Could not accept the document.');
      }
      const workflow = data.post_filing_trustee_document;
      if (workflow?.warning) {
        alert(workflow.warning);
      }
      await fetchChecklist();
    } catch (error) {
      console.error('Accept failed', error);
      alert(error.message || 'Could not accept the document.');
    }
  };

  const handleReject = async () => {
    if (!rejectionNote || !rejectingUpload) return;
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_URL}/uploaded-document/${rejectingUpload}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'rejected', review_notes: rejectionNote })
      });
      setRejectionNote('');
      setRejectingUpload(null);
      await fetchChecklist();
    } catch (error) {
      console.error('Reject failed', error);
    }
  };

  const handleDeleteUpload = async (uploadId) => {
    const confirm = window.confirm("Delete this uploaded document? This cannot be undone.");
    if (!confirm) return;
    setDeletingUploadId(uploadId);
    try {
      const token = localStorage.getItem('accessToken');
      // Try DELETE first
      let response = await fetch(`${API_URL}/uploaded-document/${uploadId}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // If backend doesn't support DELETE, fall back to POST action=delete
      if (!response.ok) {
        response = await fetch(`${API_URL}/uploaded-document/${uploadId}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'delete' }),
        });
      }

      if (!response.ok) {
        const errTxt = await response.text();
        console.error('Delete upload failed:', errTxt);
        alert('Could not delete the file.');
      } else {
        await fetchChecklist();
      }
    } catch (error) {
      console.error('Delete upload error', error);
      alert('Delete failed due to a network or server error.');
    } finally {
      setDeletingUploadId(null);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newItemName.trim()) return;
    setAddingItem(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'add',
          name: newItemName,
          description: newItemDescription,
          post_filing_debtor_requirement: newItemPostFilingRequirement,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.detail || 'Could not add the checklist item.');
      }
      await fetchChecklist();
      setNewItemName('');
      setNewItemDescription('');
      setNewItemPostFilingRequirement(false);
    } catch (error) {
      console.error('Error adding item', error);
    } finally {
      setAddingItem(false);

    }
  };

  const handlePostFilingRequirementToggle = async (item) => {
    setUpdatingPostFilingId(item.id);
    setPostFilingMessage('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'update_post_filing_requirement',
          item_id: item.id,
          enabled: !item.post_filing_debtor_requirement,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Could not update the reminder setting.');
      }
      setChecklistItems((current) => current.map((entry) => (
        entry.id === item.id
          ? { ...entry, post_filing_debtor_requirement: data.post_filing_debtor_requirement }
          : entry
      )));
      setPostFilingMessage(
        data.post_filing_debtor_requirement
          ? `Automatic debtor reminders turned on for “${item.name}.”`
          : `Automatic debtor reminders turned off for “${item.name}.”`,
      );
    } catch (error) {
      setPostFilingMessage(error.message || 'Could not update the reminder setting.');
    } finally {
      setUpdatingPostFilingId(null);
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    const confirm = window.confirm("Are you sure you want to delete this checklist item?");
    if (!confirm) return;

    setDeletingItemId(itemId);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "delete",
          item_id: itemId,
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error deleting item:", errorData);
      } else {
        setChecklistItems(prev => prev.filter(item => item.id !== itemId));
      }
    } catch (error) {
      console.error("Delete error", error);
    } finally {
      setDeletingItemId(null);
    }
  };

  async function enhanceImage(file) {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
  
    // Draw image
    ctx.drawImage(bitmap, 0, 0);
  
    // Get image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
  
    // Convert to grayscale and boost contrast
    for (let i = 0; i < data.length; i += 4) {
      // Luminance formula
      const grayscale = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  
      // Boost contrast (simple linear contrast adjustment)
      const contrast = 1.5; // You can adjust this value if needed
      const contrasted = (grayscale - 128) * contrast + 128;
  
      // Clamp to 0–255
      const final = Math.max(0, Math.min(255, contrasted));
  
      data[i] = data[i + 1] = data[i + 2] = final;
    }
  
    ctx.putImageData(imageData, 0, 0);
  
    // Export as compressed JPEG
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          const enhancedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
            type: "image/jpeg",
          });
          resolve(enhancedFile);
        },
        "image/jpeg",
        0.5 // ← low quality to reduce size
      );
    });
  }
  async function createMultiPagePDF(images, checkListName, checkListId) {
    const pageWidth = 612;
    const pageHeight = 792;
    const maxImageSize = 900; // px, max width or height
    const FIVE_MB = 5 * 1024 * 1024;
  
    // Helper: build PDF with a given JPEG quality
    async function buildPdfWithQuality(quality) {
      const pdfDoc = new jsPDF({ unit: "pt", format: "letter" });
      for (let i = 0; i < images.length; i++) {
        const imgBitmap = await createImageBitmap(images[i]);
  
        // 1) Resize image (max 900px)
        const scale = Math.min(1, maxImageSize / Math.max(imgBitmap.width, imgBitmap.height));
        const resizedW = Math.floor(imgBitmap.width * scale);
        const resizedH = Math.floor(imgBitmap.height * scale);
  
        const canvas = document.createElement("canvas");
        canvas.width = resizedW;
        canvas.height = resizedH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imgBitmap, 0, 0, resizedW, resizedH);
  
        // 2) Convert to JPEG at requested quality
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
  
        // 3) Center & scale on letter page
        const pdfScale = Math.min(pageWidth / resizedW, pageHeight / resizedH);
        const pdfW = resizedW * pdfScale;
        const pdfH = resizedH * pdfScale;
        const x = (pageWidth - pdfW) / 2;
        const y = (pageHeight - pdfH) / 2;
  
        if (i > 0) pdfDoc.addPage();
        pdfDoc.addImage(dataUrl, "JPEG", x, y, pdfW, pdfH);
      }
      return pdfDoc.output("blob");
    }
  
    // First pass: build at 100% JPEG quality, then inspect size
    let blob = await buildPdfWithQuality(1.0);
  
    // If the first pass is over 5 MB, rebuild at lower quality (0.4)
    if (blob.size > FIVE_MB) {
      console.warn(`PDF is ${(blob.size / (1024 * 1024)).toFixed(2)} MB – recompressing at 0.4`);
      blob = await buildPdfWithQuality(0.4);
    }
  
    // Generate a “safe” filename
    const safeName = checkListName
      .split("?")[0]
      .replace(/^https?:\/\//, "")
      .replace(/[|\s\/]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 10);
  
    const uniqueId =
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      uuidv4();
  
    const fileName = `${safeName}-${uniqueId}.pdf`;
    return new File([blob], fileName, { type: "application/pdf" });
  }

  // Merge multiple PDF files into a single File using PDF-lib
  async function mergePDFs(files, baseName) {
    const mergedPdf = await PDFDocument.create();
    for (const file of files) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach(page => mergedPdf.addPage(page));
    }
    const mergedPdfBytes = await mergedPdf.save();
    return new File([mergedPdfBytes], `${baseName}.pdf`, { type: 'application/pdf' });
  }
const getLatestTrusteePacket = (item) => {
  const td = item?.trustee_docs;
  if (!td) return null;

  // some serializers return single object, some return array
  const arr = Array.isArray(td) ? td : [td];

  // newest by created_at
  return arr
    .filter(Boolean)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;
};

  // --- Trustee doc packet download (presigned URLs) ---
  const presignTrusteePacket = async (packetId, kind) => {
    const token = localStorage.getItem('accessToken');
    const resp = await fetch(
      `${API_URL}/firm/trustee-doc-packets/${packetId}/presign/?kind=${(kind)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(txt || 'Failed to presign trustee doc');
    }

    const data = await resp.json();

    // Some backends return a single `url`, others return different keys.
    // Prefer a key that matches the requested `kind`.
    if (data && typeof data === 'object') {
      if (kind === 'generated_pdf') {
        return (
          data.generated_pdf_url ||
          data.pdf_url ||
          data.generated_pdf ||
          data.pdf ||
          data.url ||
          data.signed_url ||
          data.presigned_url ||
          data.download_url
        );
      }

      if (kind === 'generated_docx') {
        return (
          data.generated_docx_url ||
          data.docx_url ||
          data.generated_docx ||
          data.docx ||
          data.url ||
          data.signed_url ||
          data.presigned_url ||
          data.download_url
        );
      }

      // fallback for any other kind
      return (
        data[`${kind}_url`] ||
        data[kind] ||
        data.url ||
        data.signed_url ||
        data.presigned_url ||
        data.download_url
      );
    }

    return null;
  };

  const handleDownloadTrusteePacket = async (packetId, kind) => {
    try {
      const url = await presignTrusteePacket(packetId, kind);
      if (!url) {
        alert('Could not generate a download link.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Trustee packet download failed', e);
      alert('Download failed. Please try again.');
    }
  };
  const handleSelectFiles = async (event, checklistItemId) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    setSelectedFiles(prev => ({
      ...prev,
      [checklistItemId]: [...(prev[checklistItemId] || []), ...files],
    }));
  };

  const handleRemoveFile = (checklistItemId, indexToRemove) => {
    setSelectedFiles(prev => ({
      ...prev,
      [checklistItemId]: prev[checklistItemId].filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const handleSubmitFiles = async (checklistItemId, itemName) => {
    const files = selectedFiles[checklistItemId];
    if (!files || !files.length) return;
    setUploadingItemId(checklistItemId);

    const skipEnhancement = /photo id|social security/i.test(itemName);

    try {
      const token = localStorage.getItem('accessToken');
      const images = files.filter(f => f.type.startsWith('image/'));
      const pdfs = files.filter(f => f.type === 'application/pdf');

      if (images.length > 0) {
        const processedImages = skipEnhancement
          ? images
          : await Promise.all(images.map(f => enhanceImage(f)));
        const combinedPDF = await createMultiPagePDF(processedImages, itemName,checklistItemId);
        const formData = new FormData();
        formData.append('file', combinedPDF);
        console.log("File selected:", formData);
        await fetch(`${API_URL}/checklist-item/${checklistItemId}/upload/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }

      function makeSafeName(base, checklistId) {
        return base
          .split('?')[0]
          .replace(/^https?:\/\//, '')
          .replace(/[|\s/]+/g, '-')
          .replace(/^-+|-+$/g, '').slice(0, 10);;
      }
      const base = makeSafeName(itemName, checklistItemId);
      if (pdfs.length > 0) {
        const encryptedFiles = [];
        const plainFiles = [];
        for (const file of pdfs) {
          try {
            // Detect encryption
            await PDFDocument.load(
              await file.arrayBuffer(),
              { ignoreEncryption: false }
            );
            plainFiles.push(file);
          } catch (err) {
            encryptedFiles.push(file);
          }
        }
        // Merge & upload plain PDFs
        if (plainFiles.length > 0) {
          const mergedFile = await mergePDFs(
            plainFiles,
            `${base}${window.crypto?.randomUUID?.() || uuidv4()}`
          );
          const formData = new FormData();
          formData.append('file', mergedFile);
          await fetch(`${API_URL}/checklist-item/${checklistItemId}/upload/`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
        }
        // Upload encrypted PDFs as‐is
          for (const file of encryptedFiles) {
            // build a unique filename just like you do for merged PDFs
            const uniqueName = `${base}-${window.crypto?.randomUUID?.() || uuidv4()}.pdf`;
            // wrap the original file in a new File with your unique name
            const renamedFile = new File([file], uniqueName, { type: file.type });
            const formData = new FormData();
            formData.append('file', renamedFile);
            await fetch(`${API_URL}/checklist-item/${checklistItemId}/upload/`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: formData,
            });
          }
    }

      await fetchChecklist();
      setSelectedFiles(prev => ({ ...prev, [checklistItemId]: [] }));
      setResetKey(prev => prev + 1);

    } catch (error) {
      console.error('Upload error', error);
    } finally {
      setUploadingItemId(null);
    }
  };

  const handleMergeSelectedUploads = async (item) => {
    const itemId = item.id;
    const { ids, deleteOld, merging } = getMergeState(itemId);
    if (merging) return;

    const selectedIds = Array.from(ids);
    if (selectedIds.length < 2) {
      alert('Select at least two PDFs to merge.');
      return;
    }

    // Collect selected uploads
    const uploads = (item.uploads || [])
  .filter(u => selectedIds.includes(u.id))
  .sort((b, a) => {
    const da = new Date(a.uploaded_at || a.created_at || 0).getTime();
    const db = new Date(b.uploaded_at || b.created_at || 0).getTime();

    // oldest -> newest (created date order)
    if (da !== db) return da - db;

    // stable fallback if timestamps missing/equal
    return (a.id || 0) - (b.id || 0);
  });
    if (uploads.length < 2) {
      alert('Select at least two PDFs to merge.');
      return;
    }

    const nonPdf = uploads.filter(u => !isPdfUpload(u));
    if (nonPdf.length) {
      alert('Only PDF uploads can be merged. Please unselect non-PDF files.');
      return;
    }

    setMergeSelections(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { ids: new Set(), deleteOld: false }), merging: true, ids },
    }));

    try {
      const token = localStorage.getItem('accessToken');
      const mergeResp = await fetch(`${API_URL}/checklist-item/${itemId}/merge/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          upload_ids: uploads.map(upload => upload.id),
          delete_originals: deleteOld,
        }),
      });
      const mergeData = await mergeResp.json().catch(() => ({}));
      if (!mergeResp.ok) {
        throw new Error(mergeData.detail || 'The selected PDFs could not be merged.');
      }

      await fetchChecklist();
      clearMergeSelection(itemId);
    } catch (err) {
      console.error('Merge error', err);
      alert(err.message || 'The selected PDFs could not be merged.');
    } finally {
      setMergeSelections(prev => ({
        ...prev,
        [itemId]: { ...(prev[itemId] || { ids: new Set(), deleteOld: false }), merging: false },
      }));
    }
  };

  if (loading) return <div>Loading checklist...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-sm rounded-md p-6 mb-6 border">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-tight flex items-center space-x-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-indigo-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m-2 4h2a2 2 0 002-2v-7a2 2 0 00-2-2h-3V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2h2"
              />
            </svg>
            <span>Document Center</span>
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Upload, review, and manage all required documents for this case below.
          </p>
        </div>
      <div className="bg-gray-100 p-4 rounded mb-6">
        <h3 className="text-md font-semibold mb-2">Add a Checklist Item</h3>
        {/* Template dropdown */}
        <select
          onChange={(e) => {
            const selectedTemplate = checklistTemplates.find(t => t.label === e.target.value);
            if (selectedTemplate) {
              setNewItemName(selectedTemplate.name);
              setNewItemDescription(selectedTemplate.description);
            }
          }}
          className="w-full mb-2 p-2 border rounded bg-white"
          defaultValue=""
        >
          <option value="">Select a Template...</option>
          {checklistTemplates.map((template) => (
            <option key={template.label} value={template.label}>
              {template.label}
            </option>
          ))}
        </select>

        {/* Name Input */}
        <input
          type="text"
          placeholder="Name"
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          className="w-full mb-2 p-2 border rounded"
        />

        {/* Description Input */}
        <textarea
          placeholder="Description (optional)"
          value={newItemDescription}
          onChange={(e) => setNewItemDescription(e.target.value)}
          className="w-full mb-2 p-2 border rounded"
          rows={3}
        />

        <label className="mb-3 flex items-start gap-2 rounded-md border border-indigo-200 bg-white p-3 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={newItemPostFilingRequirement}
            onChange={(event) => setNewItemPostFilingRequirement(event.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-semibold text-gray-900">Post-filing requirement from debtor</span>
            <span className="mt-0.5 block text-xs text-gray-500">
              Email the debtor at 21, 14, 7, 3, and 1 days before the 341 until this document is received.
            </span>
          </span>
        </label>

        <button
          onClick={handleAddChecklistItem}
          disabled={addingItem}
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
        >
          {addingItem ? 'Adding...' : 'Add Checklist Item'}
        </button>
        {postFilingMessage && (
          <p className="mt-3 text-sm font-medium text-indigo-700">{postFilingMessage}</p>
        )}
      </div>

      {checklistItems.map(item => (
        <div
        key={item.id}
        className={`relative border rounded p-4 bg-white shadow transition-opacity duration-300 ${
          deletingItemId === item.id ? 'opacity-50 pointer-events-none' : ''
        }`}
        >
          {deletingItemId === item.id && (
              <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center rounded">
                <div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          <button
            onClick={() => handleDeleteChecklistItem(item.id)}
            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
            title="Delete item"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
          <button
            type="button"
            onClick={() => handlePostFilingRequirementToggle(item)}
            disabled={updatingPostFilingId === item.id}
            className={`mb-2 inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset disabled:opacity-50 ${
              item.post_filing_debtor_requirement
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                : 'bg-gray-50 text-gray-600 ring-gray-300'
            }`}
          >
            {updatingPostFilingId === item.id
              ? 'Updating…'
              : item.post_filing_debtor_requirement
                ? 'Post-filing debtor reminders on'
                : 'Mark as post-filing debtor requirement'}
          </button>

          {(() => {
            const packet = getLatestTrusteePacket(item);
            if (!packet) return null;

            const hasDocx = !!packet.generated_docx;
            const hasPdf = !!packet.generated_pdf;
            if (!hasDocx && !hasPdf) return null;

            return (
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-blue-900">Trustee Document to Download & Complete</div>
                    <div className="text-xs text-blue-800 mt-1">
                      Download this document, fill it out, and then upload it below.
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {hasPdf && (
                      <button
                        type="button"
                        onClick={() => handleDownloadTrusteePacket(packet.id, 'pdf')}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        Download PDF
                      </button>
                    )}
                    {hasDocx && (
                      <button
                        type="button"
                        onClick={() => handleDownloadTrusteePacket(packet.id, 'docx')}
                        className="inline-flex items-center rounded-md border border-blue-300 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        Download Word
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="mt-4 flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-2">
            <label className="cursor-pointer inline-block bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-blue-100">
              Choose Files / Take Photos
              <input
                key={resetKey}
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => handleSelectFiles(e, item.id, item.name)}
                className="hidden"
              />
            </label>
            <button
              onClick={() => handleSubmitFiles(item.id, item.name)}
              disabled={!selectedFiles[item.id] || uploadingItemId === item.id}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-gray-300"
            >
              {uploadingItemId === item.id ? 'Uploading...' : 'Submit Documents'}
            </button>
          </div>

          {selectedFiles[item.id]?.length > 0 && (
            <div className="text-sm text-gray-700 mb-4">
              <ul className="space-y-1">
                {selectedFiles[item.id].map((file, idx) => (
                  <li key={idx} className="flex justify-between items-center">
                    <span>{file.name}</span>
                    <button
                      onClick={() => handleRemoveFile(item.id, idx)}
                      className="text-red-600 hover:underline text-xs"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(item.uploads || []).length > 0 ? (
            <div className="mt-4 space-y-3">
              {(() => {
                const ms = getMergeState(item.id);
                const count = ms.ids.size;
                if (count < 2) return null;
                return (
                  <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-sm text-indigo-900">
                        <span className="font-semibold">Merge selected:</span> {count} file{count === 1 ? '' : 's'}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <label className="inline-flex items-center gap-2 text-sm text-indigo-900">
                          <input
                            type="checkbox"
                            checked={ms.deleteOld}
                            onChange={(e) => setMergeDeleteOld(item.id, e.target.checked)}
                          />
                          Delete originals after merge
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleMergeSelectedUploads(item)}
                            disabled={ms.merging}
                            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:bg-indigo-300"
                          >
                            {ms.merging ? 'Merging...' : 'Merge'}
                          </button>
                          <button
                            type="button"
                            onClick={() => clearMergeSelection(item.id)}
                            disabled={ms.merging}
                            className="rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-indigo-700">
                      Note: Only PDFs can be merged.
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const pdfUploads = (item.uploads || []).filter(isPdfUpload);
                if (pdfUploads.length < 2) return null;
                const selectedIds = getMergeState(item.id).ids;
                const allSelected = pdfUploads.every(upload => selectedIds.has(upload.id));
                return (
                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="text-xs text-gray-600">
                      {pdfUploads.length} PDF upload{pdfUploads.length === 1 ? '' : 's'} available to merge
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleSelectAllMergeableUploads(item)}
                      disabled={getMergeState(item.id).merging}
                      className="text-sm font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
                    >
                      {allSelected ? 'Clear selection' : 'Select all PDFs'}
                    </button>
                  </div>
                );
              })()}

              <ul className="space-y-2">
                {item.uploads.map(upload => (
                  <li key={upload.id}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={getMergeState(item.id).ids.has(upload.id)}
                        onChange={() => toggleMergeSelect(item.id, upload.id)}
                        disabled={!isPdfUpload(upload)}
                        title={isPdfUpload(upload) ? 'Select to merge' : 'Only PDFs can be merged'}
                      />
                      <div className="min-w-0">
                        <a
                          href={upload.signed_url}
                          className="text-blue-600 hover:underline break-words"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {upload.filename}
                        </a>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                          <span className="text-xs text-gray-500">{upload.status}</span>
                          <div className="flex items-center space-x-3 text-xs mt-1 sm:mt-0">
                            {upload.status === 'awaiting_review' && (
                              <>
                                <button onClick={() => handleAccept(upload.id)} className="text-green-600 hover:underline">Accept</button>
                                <button onClick={() => setRejectingUpload(upload.id)} className="text-red-600 hover:underline">Reject</button>
                              </>
                            )}
                            <button
                              onClick={() => handleDeleteUpload(upload.id)}
                              className="text-gray-500 hover:text-red-700 flex items-center"
                              title="Delete uploaded file"
                              disabled={deletingUploadId === upload.id}
                            >
                              {deletingUploadId === upload.id ? (
                                <span className="inline-flex items-center">
                                  <span className="animate-spin h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></span>
                                  Deleting...
                                </span>
                              ) : (
                                <>
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 mr-1">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                                  </svg>
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {rejectingUpload && (
                          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
                            <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
                              <h2 className="text-lg font-semibold mb-4">Reject Document</h2>
                              <textarea
                                value={rejectionNote}
                                onChange={(e) => setRejectionNote(e.target.value)}
                                placeholder="Enter reason for rejection"
                                rows={4}
                                className="w-full p-2 border rounded mb-4"
                              />
                              <div className="flex justify-end space-x-2">
                                <button onClick={() => setRejectingUpload(null)} className="bg-gray-200 px-4 py-2 rounded">Cancel</button>
                                <button onClick={handleReject} className="bg-red-600 text-white px-4 py-2 rounded">Reject</button>
                              </div>
                            </div>
                          </div>
                        )
                      }
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic mt-2">No document uploaded yet.</p>
          )}
        </div>
      ))}
    </div>
  );
}
